import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CContainer, CSpinner, CRow, CCol, CCard, CCardHeader, CCardBody, CFormSelect,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell
} from '@coreui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { apiClient } from 'services/Api';

// -------- Legends / labels (Portuguese UI text) --------
const g1Labels = [
  'Imagem da STREAM no mercado/sociedade',
  'Papel e desempenho global da STREAM na sociedade',
  'Nível de envolvimento/contributo dos colaboradores da STREAM para a missão e imagem',
  'Igualdade de Oportunidades proporcionada pela STREAM (carreira, formação e promoção)',
];

const g2Labels = [
  'Forma como recompensa os esforços individuais',
  'Forma como recompensa os esforços de grupo',
  'Forma como estabelece os objetivos individuais e de grupo',
  'Funcionamento do sistema de avaliação de desempenho',
  'Aptidão para liderar/conduzir a organização',
  'Aptidão para comunicar',
  'Aptidão para motivar e estimular a iniciativa dos colaboradores',
  'Forma como adequa o tratamento dado às pessoas, às necessidades e às situações em causa',
  'Forma como delega competências e responsabilidades',
  'Forma como lida com conflitos e problemas',
  'Forma como promove a conciliação do trabalho com a vida familiar e assuntos pessoais',
  'As condições (salário e outros benefícios) praticadas',
];

const g3Labels = [
  'Organização dos processos de trabalho',
  'Integração entre os vários departamentos',
];

const g4Labels = [
  'Mecanismos de consulta e diálogo existentes',
  'Plano de carreira disponibilizado',
  'Oportunidades criadas',
  'Política de Gestão Recursos Humanos',
];

const g5Labels = [
  'Bom ambiente de trabalho',
  'Organização pessoal/Disciplina',
  'Auto-motivação',
  'Incentivo e reconhecimento das chefias',
  'Objetivos e diversidade de tarefas',
  'Inovação',
  'Equilíbrio vida pessoal/profissional',
  'Incentivos monetários',
  'Outros',
];

const g6Labels = [
  'A sua produtividade na função que desempenha',
  'Considera de fazer a diferença para a empresa através da excelência no seu desempenho',
  'Considera-se reconhecido pelo meu mérito',
  'Considera-se respeitado como indivíduo e pelo trabalho que desenvolve',
  'Considera que a organização mostra confiança no seu trabalho',
  'A organização permite desenvolver-se como pessoa e profissional',
  'A organização permite-lhe aprender continuamente',
  'A organização permite-lhe ter novos desafios',
  'Sente que tem autonomia e responsabilidade para o desempenho da sua função',
  'A organização permite-lhe ser empreendedor e pró-ativo',
  'A organização permite-lhe fazer o que gosta',
  'Sente-se envolvido com os valores da organização',
  'Sente que tem o apoio que necessita da gestão',
  'Sente que tem equilíbrio entre o trabalho e a vida pessoal',
  'As condições financeiras são justas para a função que desempenha',
  'Considera que o trabalho mais flexível (horários, trabalhar a partir de casa, etc.) lhe permite alcançar maior produtividade no seu trabalho',
  'Considera ter um bom ambiente de trabalho',
  'Considera que os seus colegas estão envolvidos com a organização e são bons profissionais',
  'Considera que existe um bom espírito de equipa na organização',
  'Existe humor no dia a dia',
  'A comunicação na organização é fácil',
  'Tem bons meios e ferramentas para realizar o seu trabalho',
  'Considera que a sua função é útil para a organização',
  'Sente que o seu chefe tem confiança em si e no desempenho da sua função',
  'Sempre que necessário tem o apoio da sua chefia',
  'Considera que é feliz na STREAM',
  'Sente-se feliz com a função que desempenha',
  'Na generalidade, a minha vida está perto do meu ideal de vida',
];

// -------- Helpers --------
const buildYearOptions = (start = 2019) => {
  const current = new Date().getFullYear() - 1;
  const res = [];
  for (let y = start; y <= current; y++) res.push(y);
  return res.reverse();
};

const makeLikertChart = (title, values, height = 280) => ({
  chart: { type: 'column', height },
  title: { text: null },
  xAxis: { categories: values.map((_, i) => `P${i + 1}`) },
  yAxis: { min: 0, max: 6, tickInterval: 1, title: { text: '' } },
  legend: { enabled: false },
  series: [{ name: title, data: (values || []).map(Number) }],
  tooltip: { pointFormat: '<b>Média:</b> {point.y:.2f}<br/>' },
  credits: { enabled: false },
});

const makeOverallGroupsChart = (groups) => {
  const categories = groups.map(g => g.name);
  const means = groups.map(g => Number(g.mean ?? 0));
  const medians = groups.map(g => Number(g.median ?? 0));
  return {
    chart: { type: 'column', height: 320 },
    title: { text: null },
    xAxis: { categories },
    yAxis: { min: 0, max: 6, tickInterval: 1, title: { text: '' } },
    legend: { align: 'center', verticalAlign: 'bottom' },
    plotOptions: { column: { pointPadding: 0.1, borderWidth: 0 } },
    series: [
      { name: 'Média', data: means },
      { name: 'Mediana', data: medians },
    ],
    credits: { enabled: false },
  };
};

const makeGroup5CountsChart = (counts) => ({
  chart: { type: 'column', height: 280 },
  title: { text: null },
  xAxis: { categories: counts.map((_, i) => `P${i + 1}`), title: { text: '' } },
  yAxis: { min: 0, allowDecimals: false, title: { text: '' } },
  legend: { enabled: false },
  series: [{ name: 'Seleções', data: (counts || []).map(Number) }],
  credits: { enabled: false },
});

// -------- View --------
const HappinessSurveyResults = () => {
  const { year: yearParam } = useParams();
  const navigate = useNavigate();
  const currentYear = (new Date().getFullYear() - 1).toString();

  const [loading, setLoading] = useState(true);
  const [yearList, setYearList] = useState(buildYearOptions(2019));
  const [year, setYear] = useState(yearParam || currentYear);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const selectedYear = yearParam || currentYear;
        const res = await apiClient.get(`/happiness-surveys/results/${selectedYear}/`);
        setYearList(res.data?.available_years || buildYearOptions(2019));
        setData(res.data);
        setYear(String(selectedYear));
      } catch (err) {
        console.error('Error loading Happiness results:', err);
        setData(null);
        setYearList(buildYearOptions(2019));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [yearParam, currentYear]);

  if (loading || !data) {
    return (
      <CContainer className="text-center py-5">
        <CSpinner color="primary" />
      </CContainer>
    );
  }

  const overallChart = makeOverallGroupsChart([
    { name: 'Grupo 1', mean: data.group1?.mean, median: data.group1?.median },
    { name: 'Grupo 2', mean: data.group2?.mean, median: data.group2?.median },
    { name: 'Grupo 3', mean: data.group3?.mean, median: data.group3?.median },
    { name: 'Grupo 4', mean: data.group4?.mean, median: data.group4?.median },
    { name: 'Grupo 6', mean: data.group6?.mean, median: data.group6?.median },
  ]);

  return (
    <CContainer className="py-4">
      <h3 className="mb-4">Resultados — Inquérito de Felicidade</h3>

      {/* Filtro de ano */}
      <CRow className="mb-4">
        <CCol md={12}>
          <CFormSelect
            aria-label="Selecionar ano"
            value={year}
            onChange={(e) => navigate(`/inqueritos-felicidade/resultados/${e.target.value}`)}
          >
            {yearList.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </CFormSelect>
        </CCol>
      </CRow>

      {/* Informação Geral */}
      <CRow className="mb-4">
        <CCol md={12}>
          <CCard>
            <CCardHeader><strong>Informação Geral</strong></CCardHeader>
            <CCardBody>
              <CTable striped bordered hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="text-center">Média</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Mediana</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Nº de respostas</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  <CTableRow className="text-center">
                    <CTableDataCell>{data.overall?.mean != null ? Number(data.overall.mean).toFixed(2) : '-'}</CTableDataCell>
                    <CTableDataCell>{data.overall?.median != null ? Number(data.overall.median).toFixed(2) : '-'}</CTableDataCell>
                    <CTableDataCell>{data.total_responses ?? 0}</CTableDataCell>
                  </CTableRow>
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Média/Mediana por Grupo */}
      <CRow className="mb-4">
        <CCol md={12}>
          <CCard>
            <CCardHeader><strong>Média/Mediana do Inquérito por Grupo</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact highcharts={Highcharts} options={overallChart} />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Grupo 1 */}
      <CRow className="mb-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader><strong>Grupo 1</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact
                highcharts={Highcharts}
                options={makeLikertChart('Grupo 1', data.group1?.questions || [])}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader><strong>Legenda</strong></CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                {g1Labels.map((t, i) => <li key={i} className="mb-1">{t}</li>)}
              </ol>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Grupo 2 */}
      <CRow className="mb-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader><strong>Grupo 2</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact
                highcharts={Highcharts}
                options={makeLikertChart('Grupo 2', data.group2?.questions || [])}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader><strong>Legenda</strong></CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                {g2Labels.map((t, i) => <li key={i} className="mb-1">{t}</li>)}
              </ol>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Grupo 3 */}
      <CRow className="mb-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader><strong>Grupo 3</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact
                highcharts={Highcharts}
                options={makeLikertChart('Grupo 3', data.group3?.questions || [])}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader><strong>Legenda</strong></CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                {g3Labels.map((t, i) => <li key={i} className="mb-1">{t}</li>)}
              </ol>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Grupo 4 */}
      <CRow className="mb-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader><strong>Grupo 4</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact
                highcharts={Highcharts}
                options={makeLikertChart('Grupo 4', data.group4?.questions || [])}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader><strong>Legenda</strong></CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                {g4Labels.map((t, i) => <li key={i} className="mb-1">{t}</li>)}
              </ol>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Grupo 5 (contagens Top-2) */}
      <CRow className="mb-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader><strong>Grupo 5</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact
                highcharts={Highcharts}
                options={makeGroup5CountsChart(data.group5?.counts || [])}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader><strong>Legenda</strong></CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                {g5Labels.map((t, i) => <li key={i} className="mb-1">{t}</li>)}
              </ol>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Grupo 6 */}
      <CRow className="mb-5">
        <CCol md={12}>
          <CCard>
            <CCardHeader><strong>Grupo 6</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact
                highcharts={Highcharts}
                options={makeLikertChart('Grupo 6', data.group6?.questions || [], 360)}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Legenda Grupo 6 */}
      <CRow className="mb-5">
        <CCol md={12}>
          <CCard>
            <CCardHeader><strong>Legenda</strong></CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                {g6Labels.map((t, i) => <li key={i} className="mb-1">{t}</li>)}
              </ol>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default HappinessSurveyResults;
